/**
 * Dashboard Theme Configuration
 *
 * Single source of truth for dashboard visual settings.
 * Centralizes colors, chart config, and breakpoints for easy customization.
 */

// ============================================
// CHART COLORS
// ============================================
// Using the same colors as the Exported/Deleted badges for consistency
// These match the CSS variables in index.css: --accent-green, --accent-red

export const CHART_COLORS = {
  /**
   * Exported hours - matches badge styling pattern:
   * - Fill: light green for bar area
   * - Stroke: full color (for legend icons)
   */
  exported: {
    fill: "#a3d9b1",      // Muted sage green (pastel, less neon)
    stroke: "#16A34A",    // --accent-green (full color for legend)
  },
  /**
   * Unexported hours - matches badge styling pattern:
   * - Fill: light red for bar area
   * - Stroke: full color (for legend icons)
   */
  unexported: {
    fill: "#fca5a5",      // Red-300 (visible but not too saturated)
    stroke: "#EF4444",    // --accent-red (full color for legend)
  },
  /** Grid and axis styling */
  grid: {
    line: "#d1d5db",      // Gray-300
    axis: "#6b7280",      // Gray-500 (darker)
  },
};

// ============================================
// CHART CONFIGURATION
// ============================================

export const CHART_CONFIG = {
  /** Maximum bar width in pixels (prevents comically wide bars with few data points) */
  maxBarSize: 80,
  /** Border radius for top of stacked bars [topLeft, topRight, bottomLeft, bottomRight] */
  topBarRadius: [4, 4, 0, 0] as [number, number, number, number],
  /** No radius for bottom bar in stack */
  bottomBarRadius: [0, 0, 0, 0] as [number, number, number, number],
};

// ============================================
// TEXT COLORS (for tooltips, tables, cards)
// ============================================

export const TEXT_COLORS = {
  exported: {
    light: "text-green-600 dark:text-green-400",
    value: "text-green-600 dark:text-green-400",
  },
  unexported: {
    light: "text-red-500 dark:text-red-400",
    value: "text-red-500 dark:text-red-400",
  },
};

// ============================================
// SUMMARY CARD STYLES
// ============================================

export const SUMMARY_CARD_STYLES = {
  /** Base card style - compact, clean */
  base: "p-3 rounded-lg bg-card border border-border shadow-sm",
  /** Accent bar colors (the left vertical stripe) */
  accent: {
    exported: "bg-green-500 dark:bg-green-400",
    unexported: "bg-red-500 dark:bg-red-400",
    neutral: "bg-slate-400 dark:bg-slate-500",
    count: "bg-slate-400 dark:bg-slate-500",
  },
};

// ============================================
// LAYOUT BREAKPOINTS
// ============================================

export const LAYOUT = {
  /** Breakpoint for side-by-side chart layout (Tailwind xl = 1280px) */
  sideBySideBreakpoint: "xl",
  /** Chart container height */
  chartHeight: "h-80",
};
